/**
 * Snap a date to the nearest 15-minute interval.
 */
export type RoundMode = 'up' | 'down' | 'nearest';

export const snapTo15Min = (date: Date, round: RoundMode = 'nearest'): Date => {
    const minutes = date.getMinutes();
    const m15 = 15;
    let rounded: number;
    
    if (round === 'up') rounded = Math.ceil(minutes / m15) * m15;
    else if (round === 'down') rounded = Math.floor(minutes / m15) * m15;
    else rounded = Math.round(minutes / m15) * m15;

    const d = new Date(date);
    d.setMinutes(rounded);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return d;
};

/**
 * Generates a list of 24h time strings in 15m intervals.
 */
export const getTimeIntervals = (): string[] => {
    const times: string[] = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
            times.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
        }
    }
    return times;
};

/**
 * Standard HH:mm for logic (Always Local Time).
 */
export const toLogicTime = (date: Date | string): string => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "08:00";
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
};

/**
 * Standard YYYY-MM-DD for logic (Always Local Time).
 * HARDENED: Robust extraction from ISO strings to prevent UTC date shifting.
 */
export const toLogicDate = (date: Date | string): string => {
    if (!date) return new Date().toISOString().split('T')[0];
    
    // If it's a string, check if it looks like an ISO string or a simple YYYY-MM-DD
    if (typeof date === 'string') {
        // YYYY-MM-DD check
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
        
        // Removed blind split on 'T'. We MUST let new Date() parse ISO strings 
        // to convert them to local time correctly before extracting the YYYY-MM-DD.
    }

    const d = new Date(date);
    if (isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
    
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Formats a YYYY-MM-DD string for display without timezone shifting.
 */
export const formatDisplayDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr || "";
    const [y, m, d] = parts.map(Number);
    // Use local constructor to keep component values stable
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
